import { GMWStartRegistrationData } from "./GMW";
import { BroIdMetadata, EditMetadata } from "./MetaData";
import { MonitoringTube } from "./MonitoringTube";

export const registrationTypeOptions = [
  { value: "GMW_WellHeadProtector", label: "Beschermconstructie" },
  { value: "GMW_GroundLevel", label: "Bepaling maaiveld" },
  { value: "GMW_Positions", label: "Bepaling posities" },
  { value: "GMW_Insertion", label: "Buis inplaatsen" },
  { value: "GMW_Shortening", label: "Buis inkorten" },
  { value: "GMW_Lengthening", label: "Buis oplengen" },
  { value: "GMW_TubeStatus", label: "Buisstatus" },
  { value: "GMW_Owner", label: "Eigenaar" },
  { value: "GMW_GroundLevelMeasuring", label: "Inmeting maaiveld" },
  { value: "GMW_PositionsMeasuring", label: "Inmeting posities" },
  { value: "GMW_Shift", label: "Maaiveld verlegd" },
  { value: "GMW_Maintainer", label: "Onderhouder" },
  { value: "GMW_SubsequentAdditionalSurvey", label: "Gerelateerd onderzoek" },
  { value: "GMW_Removal", label: "Opgeruimd" },
] as const;
export const registrationTypeOptionsType = registrationTypeOptions.map(({ value }) => value);
export type RegistrationTypeOptions = (typeof registrationTypeOptionsType)[number];

type GMWEventBasicData = {
  eventDate: string;
  // FUTURE: Should we add dateToBeCorrected? I do not want this to always be included.
  // But we need a way to safe the original date.
};

export type GMWGroundLevelData = Pick<
  GMWStartRegistrationData,
  "wellStability" | "groundLevelStable" | "groundLevelPosition" | "groundLevelPositioningMethod"
>;

export type GMWGroundLevelMeasuringData = Pick<
  GMWStartRegistrationData,
  "groundLevelPosition" | "groundLevelPositioningMethod"
>;

export type GMWInsertionData = {
  insertedPartLength: number;
  insertedPartDiameter: number;
  insertedPartMaterial: MonitoringTube["tubeMaterial"];
} & Pick<MonitoringTube, "tubeNumber" | "tubeTopPosition" | "tubeTopPositioningMethod">;

export type MonitoringTubeLengthening = Pick<
  MonitoringTube,
  | "tubeNumber"
  | "variableDiameter"
  | "tubeTopDiameter"
  | "tubeTopPosition"
  | "tubeTopPositioningMethod"
  | "tubeMaterial"
  | "glue"
  | "plainTubePartLength"
>;

export type GMWLengtheningData = {
  wellHeadProtector?: GMWStartRegistrationData["wellHeadProtector"];
  monitoringTubes: MonitoringTubeLengthening[];
};

export type GMWMaintainerData = Pick<GMWStartRegistrationData, "maintenanceResponsibleParty">;

export type GMWOwnerData = Pick<GMWStartRegistrationData, "owner">;

export type MonitoringTubePositions = Pick<
  MonitoringTube,
  "tubeNumber" | "tubeTopPosition" | "tubeTopPositioningMethod"
>;

export type GMWPositionsData = Pick<
  GMWStartRegistrationData,
  "wellStability" | "groundLevelStable" | "groundLevelPosition" | "groundLevelPositioningMethod"
> & {
  monitoringTubes: MonitoringTubePositions[];
};

export type GMWPositionsMeasuringData = {
  monitoringTubes: MonitoringTubePositions[];
  groundLevelPosition?: GMWStartRegistrationData["groundLevelPosition"];
  groundLevelPositioningMethod?: GMWStartRegistrationData["groundLevelPositioningMethod"];
};

export type GMWRemovalData = Record<string, never>; // Empty object

export type GMWShiftData = Pick<
  GMWStartRegistrationData,
  "groundLevelPosition" | "groundLevelPositioningMethod"
>;

export type MonitoringTubeShortening = Pick<
  MonitoringTube,
  "tubeNumber" | "tubeTopPosition" | "tubeTopPositioningMethod" | "plainTubePartLength"
>;

export type GMWShorteningData = {
  wellHeadProtector?: GMWStartRegistrationData["wellHeadProtector"];
  monitoringTubes: MonitoringTubeShortening[];
};

export type MonitoringTubeStatus = Pick<MonitoringTube, "tubeNumber" | "tubeStatus">;

export type GMWTubeStatusData = {
  monitoringTubes: MonitoringTubeStatus[];
};

export type GMWWellHeadProtectorData = Pick<GMWStartRegistrationData, "wellHeadProtector">;

export type GMWSubsequentAdditionalSurveyData = Pick<GMWStartRegistrationData, "relatedSurveys">;

const emptySymbol = Symbol("EmptyObject type");
export type EmptyObject = { [emptySymbol]?: never };

// Base discriminated union for event data based on registrationType
type GMWEventData =
  | {
      registrationType: "GMW_GroundLevel";
      sourcedocumentData: GMWGroundLevelData & GMWEventBasicData;
    }
  | {
      registrationType: "GMW_GroundLevelMeasuring";
      sourcedocumentData: GMWGroundLevelMeasuringData & GMWEventBasicData;
    }
  | {
      registrationType: "GMW_Insertion";
      sourcedocumentData: GMWInsertionData & GMWEventBasicData;
    }
  | {
      registrationType: "GMW_Lengthening";
      sourcedocumentData: GMWLengtheningData & GMWEventBasicData;
    }
  | {
      registrationType: "GMW_Maintainer";
      sourcedocumentData: GMWMaintainerData & GMWEventBasicData;
    }
  | {
      registrationType: "GMW_Owner";
      sourcedocumentData: GMWOwnerData & GMWEventBasicData;
    }
  | {
      registrationType: "GMW_Positions";
      sourcedocumentData: GMWPositionsData & GMWEventBasicData;
    }
  | {
      registrationType: "GMW_PositionsMeasuring";
      sourcedocumentData: GMWPositionsMeasuringData & GMWEventBasicData;
    }
  | {
      registrationType: "GMW_Removal";
      sourcedocumentData: GMWEventBasicData;
    }
  | {
      registrationType: "GMW_Shift";
      sourcedocumentData: GMWShiftData & GMWEventBasicData;
    }
  | {
      registrationType: "GMW_Shortening";
      sourcedocumentData: GMWShorteningData & GMWEventBasicData;
    }
  | {
      registrationType: "GMW_TubeStatus";
      sourcedocumentData: GMWTubeStatusData & GMWEventBasicData;
    }
  | {
      registrationType: "GMW_WellHeadProtector";
      sourcedocumentData: GMWWellHeadProtectorData & GMWEventBasicData;
    }
  | {
      registrationType: "GMW_SubsequentAdditionalSurvey";
      sourcedocumentData: GMWSubsequentAdditionalSurveyData & GMWEventBasicData;
    };

export type GMWEventRegistrationType = GMWEventData["registrationType"];

type GMWEventBase = {
  registrationType: GMWEventRegistrationType;
  sourcedocumentData: GMWEventBasicData;
};
export type CreateGMWEventBase = GMWEventBase & { metadata: BroIdMetadata };
export type EditGMWEventBase = GMWEventBase & { metadata: EditMetadata };

// Create and Edit tasks with their respective metadata
export type CreateGMWEventTask<T extends GMWEventRegistrationType = GMWEventRegistrationType> =
  Extract<GMWEventData, { registrationType: T }> & { metadata: BroIdMetadata };

export type EditGMWEventTask<T extends GMWEventRegistrationType = GMWEventRegistrationType> =
  Extract<GMWEventData, { registrationType: T }> & { metadata: EditMetadata };

export type GMWEventTask<T extends GMWEventRegistrationType = GMWEventRegistrationType> = Extract<
  GMWEventData,
  { registrationType: T }
> & {
  metadata: BroIdMetadata | EditMetadata;
};
